// @ts-strict-ignore
import type { BrowseDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/browse-domain-config'
import type { SearchDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/search-domain-config'
import type { ToolConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/tool-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { ConfigService } from '@empathyco/playboard-core-domain'
import type { Store } from 'vuex'
import type { PivotableToolConfig, ToolConfigPivot } from '../models/domain/pivotable-tool-config'
import type { EqualizeCrudService } from '../modules/equalize'
import type { SearchToolingService } from '../services/interfaces'
import type { ContextProvider, ToolConfigProvider } from './interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { interfaces } from 'inversify'
import { getDefaultEndpointId } from '../composables/use-endpoints'
import { TYPES } from '../config/inversify.types'
import { FeatureIdEnum } from '../enums'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import Context = interfaces.Context

export function getPlatformToolConfigProvider(context: Context): ToolConfigProvider {
  return (tool: string, domain?: DomainEnum) => {
    switch (tool) {
      case FeatureIdEnum.EQUALIZE:
        return getPlatformEqualizeConfigProvider(context)(tool, domain)
      case FeatureIdEnum.CONFIGURATION:
      case FeatureIdEnum.FACETS:
        if (domain === DomainEnum.BROWSE) {
          return getInstanceToolConfigProvider(context)(tool, domain)
        }
        return getPlatformQueryRankingConfigProvider(context)(tool, domain)
      case FeatureIdEnum.RELATED_TAGS:
      case FeatureIdEnum.NEXT_QUERIES:
        return getPlatformCurationToolsConfigProvider(context)(tool, domain)
      default:
        return getInstanceToolConfigProvider(context)(tool, domain)
    }
  }
}

async function getPivotsFromToolingService(
  container: Context['container'],
): Promise<ToolConfigPivot[]> {
  const toolingService = container.get<SearchToolingService>(TYPES.SearchToolingService)
  const { pivots } = await toolingService.getInstanceConfig()

  return pivots
}

function getPlatformEqualizeConfigProvider({ container }: Context): ToolConfigProvider {
  return async () => {
    const crudService = container.get<EqualizeCrudService>(TYPES.EqualizeCrudService)
    const { results: defaultConfigs } = await crudService.list({
      page: {
        rows: 100,
      },
      isDefault: true,
    })

    const pivots = await getPivotsFromToolingService(container)

    const searchDefaultConfig =
      defaultConfigs.find(config => config.endpointId === getDefaultEndpointId()) ||
      defaultConfigs[0]

    return {
      default: searchDefaultConfig,
      allDefaultEndpoints: defaultConfigs,
      pivots,
    }
  }
}

function getPlatformQueryRankingConfigProvider({ container }: Context): ToolConfigProvider {
  return async () => {
    const pivots = await getPivotsFromToolingService(container)

    return {
      pivots,
    }
  }
}

function getPlatformCurationToolsConfigProvider(context: Context): ToolConfigProvider {
  const { container } = context

  return async (tool, domain): Promise<PivotableToolConfig> => {
    const service = container.get<ConfigService>(TYPES.ConfigService)

    if (!service) {
      return await getInstanceToolConfigProvider(context)(FeatureIdEnum.CURATION_TOOLS, domain)
    }

    const { instanceId: instance } = container.get<ContextProvider>(TYPES.ContextProvider)()
    const response = await service.read({ instance, service: 'querysignals' })
    const data = response.data as { pivotCuratedFields: Array<string> }
    const pivots = data.pivotCuratedFields.map(pivot => ({ name: pivot }))
    return {
      pivots,
    }
  }
}

function getInstanceToolConfigProvider({ container }: Context): ToolConfigProvider {
  return (tool: string, domain?: DomainEnum): Promise<PivotableToolConfig> => {
    const store = container.get<Store<unknown>>(TYPES.Store)
    const instanceConfig: Instance =
      store.getters[`${CONFIG_CONSTS.moduleName}/${CONFIG_CONSTS.getters.currentInstance}`]
    if (!domain) {
      domain = container.get<ContextProvider>(TYPES.ContextProvider)().domain
    }
    if (!instanceConfig || !domain) {
      return Promise.reject('No pivots found for current instance')
    }
    const domainConfig = instanceConfig?.domains[domain] as SearchDomainConfig | BrowseDomainConfig
    const toolConfig = domainConfig?.tools[tool] as ToolConfig
    const pivots = toolConfig?.pivotIds?.map(pivotId => ({ name: pivotId })) || []
    return Promise.resolve({ pivots })
  }
}
